import { DEFAULT_PAGE_SIZE } from '../constants/pagination'
import { apiService } from './api.service'

export interface DocumentWorkflowDefinitionStepActionBranch {
  id?: number
  position: number
  condition_query: string
  next_step_id?: number | null
  back_to_step_id?: number | null
  end_workflow: boolean
  assign_workflow_status_id?: number | null
}

export interface DocumentWorkflowDefinitionStepAction {
  id?: number
  name: string
  next_step_id?: number | null
  back_to_step_id?: number | null
  end_workflow: boolean
  assign_workflow_status_id?: number | null
  comment_required?: boolean
  branches?: DocumentWorkflowDefinitionStepActionBranch[]
}

export type NotificationDelivery = 'inherit' | 'websocket_only' | 'email_only' | 'websocket_and_email'

export interface DocumentWorkflowDefinitionStepEntryCondition {
  id?: number
  position: number
  condition_query: string
}

export interface DocumentWorkflowDefinitionStep {
  id?: number
  workflow_status_id: number
  name?: string
  position: number
  assigned_user_ids?: number[]
  assigned_group_ids?: number[]
  assigned_dynamic_recipients?: string[]
  notification_delivery?: NotificationDelivery
  actions?: DocumentWorkflowDefinitionStepAction[]
  entry_conditions?: DocumentWorkflowDefinitionStepEntryCondition[]
}

export interface DocumentWorkflowDefinition {
  id: number
  name: string
  slug: string
  description: string
  enabled: boolean
  steps?: DocumentWorkflowDefinitionStep[]
  applicable_document_type_ids?: number[]
}

/** Za create/update: grane koriste iste `*_index` semantike kao akcije (0-based indeks u steps[]). */
export interface DocumentWorkflowDefinitionStepActionBranchWrite {
  position?: number
  condition_query: string
  next_step_index?: number | null
  back_to_step_index?: number | null
  end_workflow?: boolean
  assign_workflow_status_id?: number | null
}

/** Za create/update: akcije koriste next_step_index / back_to_step_index (0-based indeks u steps[]) */
export interface DocumentWorkflowDefinitionStepActionWrite {
  id?: number
  name: string
  next_step_index?: number | null
  back_to_step_index?: number | null
  end_workflow: boolean
  assign_workflow_status_id?: number | null
  comment_required?: boolean
  branches?: DocumentWorkflowDefinitionStepActionBranchWrite[]
}

export interface DocumentWorkflowDefinitionStepEntryConditionWrite {
  position?: number
  condition_query: string
}

export interface DocumentWorkflowDefinitionStepWrite {
  id?: number
  workflow_status_id: number
  name?: string
  position: number
  assigned_user_ids?: number[]
  assigned_group_ids?: number[]
  assigned_dynamic_recipients?: string[]
  notification_delivery?: NotificationDelivery
  actions?: DocumentWorkflowDefinitionStepActionWrite[]
  entry_conditions?: DocumentWorkflowDefinitionStepEntryConditionWrite[]
}

export interface DocumentWorkflowDefinitionWrite {
  id?: number
  name: string
  slug: string
  description: string
  enabled?: boolean
  steps?: DocumentWorkflowDefinitionStepWrite[]
  applicable_document_type_ids?: number[]
}

export interface ApiResponseList<T> {
  results: T[]
  count: number
}

class DocumentWorkflowDefinitionService {
  async listAll(): Promise<{ results: DocumentWorkflowDefinition[] }> {
    const response = await apiService.get<{ results: DocumentWorkflowDefinition[] }>(
      'document_workflow_definitions/',
      { params: { page_size: 10000 } }
    )
    return response.data
  }

  async listFiltered(
    page: number,
    _ordering?: string,
    sortField?: string,
    sortReverse?: boolean,
    nameFilter?: string,
    _includeDocumentCount?: boolean,
    extraParams?: Record<string, unknown>
  ): Promise<ApiResponseList<DocumentWorkflowDefinition>> {
    const params: Record<string, unknown> = {
      page,
      page_size: (extraParams?.page_size as number) ?? DEFAULT_PAGE_SIZE,
    }
    if (sortField) {
      params.ordering = (sortReverse ? '-' : '') + sortField
    }
    if (nameFilter) {
      params.name__icontains = nameFilter
    }
    if (extraParams) {
      Object.assign(params, extraParams)
    }
    const response = await apiService.get<ApiResponseList<DocumentWorkflowDefinition>>(
      'document_workflow_definitions/',
      { params }
    )
    return response.data
  }

  async get(id: number): Promise<DocumentWorkflowDefinition> {
    const response = await apiService.get<DocumentWorkflowDefinition>(`document_workflow_definitions/${id}/`)
    return response.data
  }

  async create(data: DocumentWorkflowDefinitionWrite): Promise<DocumentWorkflowDefinition> {
    const response = await apiService.post<DocumentWorkflowDefinition>('document_workflow_definitions/', data)
    return response.data
  }

  async update(id: number, data: Partial<DocumentWorkflowDefinitionWrite>): Promise<DocumentWorkflowDefinition> {
    const response = await apiService.patch<DocumentWorkflowDefinition>(`document_workflow_definitions/${id}/`, data)
    return response.data
  }

  async delete(id: number): Promise<void> {
    await apiService.delete(`document_workflow_definitions/${id}/`)
  }
}

export const documentWorkflowDefinitionService = new DocumentWorkflowDefinitionService()
